import * as React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import Title from "../Common/Title";
import {
  getPaginatedEstimates,
  deleteEstimateByEstimateID,
  bulkUpdateEstimateStatus,
} from "../../Utils/vendor-customer-service";
import {
  useLoaderData,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { columns } from "./estimate-constant";
import ReceiptIcon from "@mui/icons-material/Receipt";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";
import { DataGrid } from "@mui/x-data-grid";

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const BULK_MAX_SELECTION = 50;

const BULK_ACTIONS_BY_TAB = {
  0: [{ status: "INPROGRESS", label: "Change to INPROGRESS", color: "warning" }],
  1: [
    { status: "COMPLETED", label: "Change to COMPLETED", color: "success" },
    { status: "CANCELLED", label: "Change to CANCELLED", color: "error" },
  ],
  2: [],
  3: [{ status: "NEW", label: "Change to NEW", color: "warning" }],
};

function Estimate(props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: vendors } = useLoaderData();
  const [estimateResults, setEstimatesResults] = React.useState([]);
  const [count, setCount] = React.useState();

  // Initialize state from URL parameters, location state, sessionStorage, or defaults
  const getInitialState = () => {
    // Check URL params first
    const urlTab = searchParams.get("tab");
    const urlPage = searchParams.get("page");
    const urlPageSize = searchParams.get("pageSize");

    // Check location state
    const stateTab = location.state?.tab;
    const statePage = location.state?.page;
    const statePageSize = location.state?.pageSize;

    // Check sessionStorage as fallback
    let sessionState = null;
    try {
      const stored = sessionStorage.getItem("estimateReturnState");
      if (stored) {
        sessionState = JSON.parse(stored);
        // Clear after reading
        sessionStorage.removeItem("estimateReturnState");
      }
    } catch (e) {
      console.warn("Failed to parse sessionStorage state:", e);
    }

    return {
      tab: parseInt(urlTab || stateTab || sessionState?.tab || "0"),
      page: parseInt(urlPage || statePage || sessionState?.page || "0"),
      pageSize: parseInt(
        urlPageSize || statePageSize || sessionState?.pageSize || "25"
      ),
    };
  };

  const initialState = getInitialState();
  const [paginationModel, setPaginationModel] = React.useState({
    page: initialState.page,
    pageSize: initialState.pageSize,
  });
  const [value, setValue] = React.useState(initialState.tab);

  // Ensure the value is always a number and within valid range
  const normalizedValue = Math.max(0, Math.min(3, value));
  const [loading, setLoading] = React.useState(false);
  const [vendorName, setVendorName] = React.useState({});
  const [rowSelectionModel, setRowSelectionModel] = React.useState([]);
  const [bulkTargetStatus, setBulkTargetStatus] = React.useState(null);
  const [openBulkConfirm, setOpenBulkConfirm] = React.useState(false);
  const [openBulkDeleteConfirm, setOpenBulkDeleteConfirm] =
    React.useState(false);
  const [bulkUpdating, setBulkUpdating] = React.useState(false);
  const [bulkResultDialog, setBulkResultDialog] = React.useState({
    open: false,
    title: "",
    message: "",
  });

  const showBulkResultDialog = (title, message) => {
    setBulkResultDialog({ open: true, title, message });
  };

  const closeBulkResultDialog = () => {
    setBulkResultDialog({ open: false, title: "", message: "" });
  };

  const statues = ["NEW", "INPROGRESS", "COMPLETED", "CANCELLED"];
  const bulkActions = BULK_ACTIONS_BY_TAB[normalizedValue] || [];
  const currentStatus = statues[normalizedValue];
  const canBulkDelete = normalizedValue === 2 || normalizedValue === 3;
  const showBulkToolbar =
    rowSelectionModel.length > 0 &&
    (bulkActions.length > 0 || canBulkDelete);
  const allowCheckboxSelection =
    bulkActions.length > 0 || canBulkDelete;

  // Update URL when state changes
  React.useEffect(() => {
    const newSearchParams = new URLSearchParams();
    newSearchParams.set("tab", normalizedValue.toString());
    newSearchParams.set("page", paginationModel.page.toString());
    newSearchParams.set("pageSize", paginationModel.pageSize.toString());
    setSearchParams(newSearchParams, { replace: true });
  }, [normalizedValue, paginationModel, setSearchParams]);

  // Handle state restoration from navigation
  React.useEffect(() => {
    if (location.state) {
      const { tab, page, pageSize } = location.state;
      console.log("Location state restoration:", { tab, page, pageSize });
      console.log("Current state:", {
        value,
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
      });

      if (tab !== undefined && tab !== normalizedValue) {
        console.log("Setting tab from location state:", tab);
        setValue(tab);
      }
      if (page !== undefined && page !== paginationModel.page) {
        console.log("Setting page from location state:", page);
        setPaginationModel((prev) => ({ ...prev, page }));
      }
      if (pageSize !== undefined && pageSize !== paginationModel.pageSize) {
        console.log("Setting pageSize from location state:", pageSize);
        setPaginationModel((prev) => ({ ...prev, pageSize }));
      }

      // Clear the location state after restoration to prevent issues
      window.history.replaceState({}, document.title);
    }
  }, [
    location.state,
    normalizedValue,
    paginationModel.page,
    paginationModel.pageSize,
  ]);

  // Additional effect to sync URL params with component state on mount
  React.useEffect(() => {
    const urlTab = searchParams.get("tab");
    const urlPage = searchParams.get("page");
    const urlPageSize = searchParams.get("pageSize");

    console.log("Mount effect - URL params:", { urlTab, urlPage, urlPageSize });
    console.log("Current state:", {
      value,
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
    });

    if (urlTab !== null && parseInt(urlTab) !== normalizedValue) {
      console.log("Setting tab from URL:", urlTab);
      setValue(parseInt(urlTab));
    }
    if (urlPage !== null && parseInt(urlPage) !== paginationModel.page) {
      console.log("Setting page from URL:", urlPage);
      setPaginationModel((prev) => ({ ...prev, page: parseInt(urlPage) }));
    }
    if (
      urlPageSize !== null &&
      parseInt(urlPageSize) !== paginationModel.pageSize
    ) {
      console.log("Setting pageSize from URL:", urlPageSize);
      setPaginationModel((prev) => ({
        ...prev,
        pageSize: parseInt(urlPageSize),
      }));
    }
  }, []); // Only run on mount

  React.useEffect(() => {
    const obj = {};
    // eslint-disable-next-line array-callback-return
    vendors.map((vendor) => {
      obj[vendor?.vendorID] =
        vendor?.name + " " + (vendor?.nickName ? `(${vendor?.nickName})` : "");
    });
    setVendorName(obj);
  }, [vendors]);

  React.useEffect(() => {
    fetchEstimates(paginationModel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, normalizedValue]);

  const fetchEstimates = (model) => {
    setLoading(true);
    let res = getPaginatedEstimates(
      model.pageSize,
      model.page,
      statues[normalizedValue]
    );
    res.then((response) => {
      if (response?.status === 200) {
        const { count, rows } = response?.data;
        setCount(count);
        setEstimatesResults(rows);
        setLoading(false);
      }
    });
  };

  const deleteEstimate = (estimateId) => {
    if (
      // eslint-disable-next-line no-restricted-globals
      confirm(`Are you sure you want to delete this estimate?
      This action cannot be undone.`)
    ) {
      setLoading(true);
      let res = deleteEstimateByEstimateID(estimateId);
      res.then((response) => {
        if (response?.status === 204) {
          setLoading(false);
          fetchEstimates(paginationModel);
        }
      });
    }
  };

  const handleBulkActionClick = (status) => {
    if (!rowSelectionModel.length) return;
    // NEW → INPROGRESS: no confirmation (same as detail)
    if (currentStatus === "NEW" && status === "INPROGRESS") {
      applyBulkStatusChange(status);
      return;
    }
    setBulkTargetStatus(status);
    setOpenBulkConfirm(true);
  };

  const applyBulkStatusChange = (status) => {
    setBulkUpdating(true);
    setLoading(true);
    bulkUpdateEstimateStatus(rowSelectionModel, status).then((response) => {
      setBulkUpdating(false);
      setOpenBulkConfirm(false);
      setBulkTargetStatus(null);

      if (response?.status === 200) {
        const { updated, failed, sideEffectFailed } = response.data || {};
        const updatedCount = updated?.length || 0;
        const failedCount = failed?.length || 0;
        const sideEffectCount = sideEffectFailed?.length || 0;

        let message = `Success: ${updatedCount}\nFailed: ${failedCount}`;
        if (sideEffectCount > 0) {
          message += `\nEstimate-again issues: ${sideEffectCount}`;
        }
        showBulkResultDialog("Bulk Status Update Result", message);

        setRowSelectionModel([]);
        fetchEstimates(paginationModel);
      } else {
        setLoading(false);
        showBulkResultDialog(
          "Bulk Status Update Failed",
          "Bulk status update failed. Please try again."
        );
      }
    });
  };

  const handleConfirmBulkStatus = () => {
    if (!bulkTargetStatus) return;
    applyBulkStatusChange(bulkTargetStatus);
  };

  const handleBulkDeleteClick = () => {
    if (!rowSelectionModel.length || !canBulkDelete) return;
    setOpenBulkDeleteConfirm(true);
  };

  const applyBulkDelete = async () => {
    const ids = rowSelectionModel.slice(0, BULK_MAX_SELECTION);
    if (!ids.length) return;

    setBulkUpdating(true);
    setLoading(true);

    let deletedCount = 0;
    let failedCount = 0;

    for (const estimateId of ids) {
      try {
        const response = await deleteEstimateByEstimateID(estimateId);
        if (response?.status === 204) {
          deletedCount += 1;
        } else {
          failedCount += 1;
        }
      } catch (e) {
        failedCount += 1;
      }
    }

    setBulkUpdating(false);
    setOpenBulkDeleteConfirm(false);

    showBulkResultDialog(
      "Bulk Delete Result",
      `Success: ${deletedCount}\nFailed: ${failedCount}`
    );

    setRowSelectionModel([]);
    fetchEstimates(paginationModel);
  };

  const handleSelectionChange = (newSelection) => {
    const selection = Array.isArray(newSelection)
      ? newSelection
      : newSelection?.ids
        ? Array.from(newSelection.ids)
        : [];

    if (selection.length > BULK_MAX_SELECTION) {
      showBulkResultDialog(
        "Selection Limit",
        `You can select up to ${BULK_MAX_SELECTION} estimates at a time.`
      );
      setRowSelectionModel(selection.slice(0, BULK_MAX_SELECTION));
      return;
    }
    setRowSelectionModel(selection);
  };

  const coloumnWithMenu = columns.map((column, idx) => {
    if (column.field === "menu") {
      return {
        ...column,
        flex: 1,
        renderCell: (params) => (
          <>
            <span>
              <Tooltip title="Show Estimate" placement="top">
                <IconButton
                  onClick={() => {
                    // Navigate with current state as query parameters
                    const currentState = {
                      tab: normalizedValue,
                      page: paginationModel.page,
                      pageSize: paginationModel.pageSize,
                      status: statues[normalizedValue],
                    };

                    navigate(
                      `/estimate/${params?.row?.vendorID}/${params?.row?.estimateId}?returnTab=${normalizedValue}&returnPage=${paginationModel.page}&returnPageSize=${paginationModel.pageSize}&returnStatus=${statues[normalizedValue]}`,
                      {
                        state: currentState,
                        replace: false,
                      }
                    );
                  }}
                  color="info"
                >
                  <ReceiptIcon />
                </IconButton>
              </Tooltip>
              {(normalizedValue === 2 || normalizedValue === 3) && (
                <Tooltip title="Delete Estimate" placement="top">
                  <IconButton
                    onClick={() => deleteEstimate(params?.row?.estimateId)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )}
            </span>
          </>
        ),
      };
    }

    if (column.field === "name") {
      return {
        ...column,
        renderCell: (params) => (
          <>
            <strong>{vendorName[params?.row?.vendorID]}</strong>
          </>
        ),
      };
    }
    return column;
  });

  const handleChange = (event, newValue) => {
    setValue(newValue);
    setRowSelectionModel([]);
    // Reset to first page when changing tabs
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
    setRowSelectionModel([]);
  };

  console.log(
    "Rendering Tabs with value:",
    value,
    "type:",
    typeof value,
    "normalized:",
    normalizedValue
  );

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="en-gb">
      <Box sx={{ borderBottom: 1, borderColor: "divider", marginBottom: 5 }}>
        <Tabs
          value={normalizedValue}
          onChange={handleChange}
          aria-label="basic tabs example"
        >
          <Tab label="NEW" {...a11yProps(0)} />
          <Tab label="INPROGRESS" {...a11yProps(1)} />
          <Tab label="COMPLETED" {...a11yProps(2)} />
          <Tab label="CANCELLED" {...a11yProps(3)} />
        </Tabs>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={10}>
          <Title>Estimates</Title>
        </Grid>
        <Grid item xs={12}>
          {showBulkToolbar && (
            <Paper
              sx={{
                p: 2,
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography variant="body1">
                <strong>{rowSelectionModel.length}</strong> selected
                {rowSelectionModel.length >= BULK_MAX_SELECTION
                  ? ` (max ${BULK_MAX_SELECTION})`
                  : ""}
              </Typography>
              {bulkActions.map((action) => (
                <Button
                  key={action.status}
                  variant="contained"
                  color={action.color}
                  disabled={bulkUpdating || loading}
                  startIcon={<ChangeCircleIcon />}
                  onClick={() => handleBulkActionClick(action.status)}
                >
                  {action.label}
                </Button>
              ))}
              {canBulkDelete && (
                <Button
                  variant="contained"
                  color="error"
                  disabled={bulkUpdating || loading}
                  startIcon={<DeleteIcon />}
                  onClick={handleBulkDeleteClick}
                >
                  Delete selected
                </Button>
              )}
              <Button
                variant="outlined"
                disabled={bulkUpdating}
                onClick={() => setRowSelectionModel([])}
              >
                Clear selection
              </Button>
            </Paper>
          )}
          <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
            <React.Fragment>
              <DataGrid
                rows={estimateResults}
                columns={coloumnWithMenu}
                getRowId={(row) => row.estimateId}
                checkboxSelection={allowCheckboxSelection}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={handleSelectionChange}
                keepNonExistentRowsSelected={false}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 10,
                    },
                  },
                }}
                sx={{
                  boxShadow: 2,
                  border: 2,
                  borderColor: "primary.dark",
                  "& .MuiDataGrid-cell:hover": {
                    color: "primary.main",
                  },
                  "& .table--header": {
                    backgroundColor: "rgb(231 225 225 / 60%)",
                    fontFamily: "inherit",
                  },
                }}
                pagination
                paginationMode="server"
                paginationModel={paginationModel}
                pageSizeOptions={[25]}
                rowCount={count}
                onPaginationModelChange={handlePaginationModelChange}
                density="comfortable"
                loading={loading}
                disableRowSelectionOnClick
              />
            </React.Fragment>
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={openBulkConfirm}
        disableEscapeKeyDown
        aria-labelledby="bulk-status-dialog-title"
        aria-describedby="bulk-status-dialog-description"
      >
        <DialogTitle id="bulk-status-dialog-title">
          Bulk Status Change
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="bulk-status-dialog-description">
            Are you sure you want to change status of{" "}
            <strong>{rowSelectionModel.length}</strong> estimate(s) from{" "}
            <strong>{currentStatus}</strong> to{" "}
            <strong>{bulkTargetStatus}</strong>? This action cannot be undone.
            <br />
            <br />
            क्या आप वाकई स्थिति बदलना चाहते हैं? -{" "}
            <strong>{bulkTargetStatus}</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenBulkConfirm(false);
              setBulkTargetStatus(null);
            }}
            disabled={bulkUpdating}
          >
            CLOSE
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmBulkStatus}
            disabled={bulkUpdating}
            autoFocus
          >
            Yes, Change Status to {bulkTargetStatus}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openBulkDeleteConfirm}
        disableEscapeKeyDown
        aria-labelledby="bulk-delete-dialog-title"
        aria-describedby="bulk-delete-dialog-description"
      >
        <DialogTitle id="bulk-delete-dialog-title">
          Bulk Delete Estimates
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="bulk-delete-dialog-description">
            Are you sure you want to delete{" "}
            <strong>
              {Math.min(rowSelectionModel.length, BULK_MAX_SELECTION)}
            </strong>{" "}
            estimate(s)? This action cannot be undone.
            <br />
            <br />
            क्या आप वाकई इन अनुमानों को हटाना चाहते हैं?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenBulkDeleteConfirm(false)}
            disabled={bulkUpdating}
          >
            CLOSE
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={applyBulkDelete}
            disabled={bulkUpdating}
            autoFocus
          >
            Yes, Delete Selected
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkResultDialog.open}
        onClose={closeBulkResultDialog}
        aria-labelledby="bulk-result-dialog-title"
        aria-describedby="bulk-result-dialog-description"
      >
        <DialogTitle id="bulk-result-dialog-title">
          {bulkResultDialog.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="bulk-result-dialog-description"
            component="div"
            sx={{ whiteSpace: "pre-line" }}
          >
            {bulkResultDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={closeBulkResultDialog}
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}

export default Estimate;
